import { useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { logout } from "../store/slices/authSlice";

// Global flag to pause inactivity timer (e.g., during session recording)
let isInactivityPaused = false;

// Custom event to notify timer to restart after resume
const INACTIVITY_RESUME_EVENT = "inactivity-timer-resume";

/**
 * Pause the inactivity timer globally (e.g., during session recording)
 */
export const pauseInactivityTimer = () => {
  isInactivityPaused = true;
};

/**
 * Resume the inactivity timer globally and restart the timer
 */
export const resumeInactivityTimer = () => {
  isInactivityPaused = false;
  // Dispatch custom event to restart the timer
  document.dispatchEvent(new CustomEvent(INACTIVITY_RESUME_EVENT));
};

/**
 * Check if inactivity timer is paused
 */
export const isInactivityTimerPaused = () => isInactivityPaused;

/**
 * Hook to automatically log out user after specified minutes of inactivity
 * @param inactivityMinutes - Number of minutes of inactivity before auto logout (default: 60, set to 0 to disable)
 */
export const useInactivityTimer = (inactivityMinutes: number = 60) => {
  const dispatch = useDispatch();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inactivityTimeMs = inactivityMinutes * 60 * 1000;

  const handleLogout = useCallback(() => {
    // Don't logout if inactivity is paused (e.g., during session recording)
    if (isInactivityPaused) {
      return;
    }
    console.log("Auto logout due to inactivity");
    dispatch(logout());
  }, [dispatch]);

  const resetTimer = useCallback(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Don't set new timeout if paused
    if (isInactivityPaused) {
      return;
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      handleLogout();
    }, inactivityTimeMs);
  }, [inactivityTimeMs, handleLogout]);

  useEffect(() => {
    // Don't set up inactivity timer if inactivityMinutes is 0 or less
    if (inactivityMinutes <= 0) {
      return;
    }

    // List of events that indicate user activity
    const events: (keyof DocumentEventMap)[] = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    // Initialize timer on mount
    resetTimer();

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, resetTimer);
    });

    // Listen for resume event to restart timer after pause
    const handleResume = () => {
      resetTimer();
    };
    document.addEventListener(INACTIVITY_RESUME_EVENT, handleResume);

    // Cleanup function
    return () => {
      // Remove event listeners
      events.forEach((event) => {
        document.removeEventListener(event, resetTimer);
      });
      document.removeEventListener(INACTIVITY_RESUME_EVENT, handleResume);

      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [resetTimer, inactivityMinutes]);
};
